import type { ComponentChildren } from 'preact'

// Typography variant types
export type TypographyVariant =
  | 'heading-1'
  | 'heading-2'
  | 'heading-3'
  | 'heading-4'
  | 'heading-5'
  | 'heading-6'
  | 'body-lg'
  | 'body'
  | 'body-sm'
  | 'small'
  | 'extra-small'

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6

export type TextVariant = 'large' | 'base' | 'small' | 'extra-small'

export type TextElement =
  | 'span'
  | 'p'
  | 'div'
  | 'strong'
  | 'em'
  | 'mark'
  | 'label'

export type HeadingElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

// Color options from the design system
export type TypographyColor =
  | 'primary'
  | 'secondary'
  | 'white'
  | 'navy'
  | 'text-gray'
  | 'inherit'

// Base props shared across typography components
export interface BaseTypographyProps {
  children: ComponentChildren
  className?: string
  color?: TypographyColor
}

// Typography component props
export interface TypographyProps extends BaseTypographyProps {
  variant: TypographyVariant
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div'
}

// Heading component props
export interface HeadingProps extends BaseTypographyProps {
  level: HeadingLevel
  as?: HeadingElement
  responsive?: boolean
}

// Text component props
export interface TextProps extends BaseTypographyProps {
  variant?: TextVariant
  as?: TextElement
  truncate?: boolean
  htmlFor?: string // For label elements
}

// Style mappings
export const TYPOGRAPHY_STYLES: Record<TypographyVariant, string> = {
  'heading-1':
    'text-heading-1 sm:text-heading-1-mobile font-bold tracking-tight text-cencert-navy',
  'heading-2':
    'text-heading-2 sm:text-heading-2-mobile font-semibold tracking-tight text-cencert-navy',
  'heading-3':
    'text-heading-3 sm:text-heading-3-mobile font-semibold text-cencert-navy',
  'heading-4': 'text-heading-4 font-semibold text-cencert-navy',
  'heading-5': 'text-heading-5 font-semibold text-cencert-navy',
  'heading-6': 'text-heading-6 font-semibold text-cencert-navy',
  'body-lg': 'text-body-lg text-cencert-text-gray',
  body: 'text-body text-cencert-text-gray',
  'body-sm': 'text-body-sm text-cencert-text-gray',
  small: 'text-small text-cencert-text-gray',
  'extra-small': 'text-extra-small text-cencert-text-gray',
}

export const COLOR_STYLES: Record<TypographyColor, string> = {
  primary: 'text-cencert-blue',
  secondary: 'text-cencert-text-gray',
  white: 'text-cencert-white',
  navy: 'text-cencert-navy',
  'text-gray': 'text-cencert-text-gray',
  inherit: 'text-inherit',
}

// Default HTML elements for each variant
export const DEFAULT_ELEMENTS: Record<TypographyVariant, string> = {
  'heading-1': 'h1',
  'heading-2': 'h2',
  'heading-3': 'h3',
  'heading-4': 'h4',
  'heading-5': 'h5',
  'heading-6': 'h6',
  'body-lg': 'p',
  body: 'p',
  'body-sm': 'p',
  small: 'span',
  'extra-small': 'span',
}

// Text variant to typography variant mapping
export const TEXT_VARIANT_MAP: Record<TextVariant, TypographyVariant> = {
  large: 'body-lg',
  base: 'body',
  small: 'small',
  'extra-small': 'extra-small',
}
